import { useState, type FormEvent } from "react";
import { Button } from "../../shared/ui/button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import {
  useAiAnalysisSettings,
  useSaveAiAnalysisSettings,
  type AiAnalysisSettings,
  type AiAnalysisSettingsInput,
} from "./queries.js";

export function AiAnalysisSettingsPanel() {
  const query = useAiAnalysisSettings();
  const save = useSaveAiAnalysisSettings();
  const [notice, setNotice] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);
  const reload = () => {
    save.reset();
    setNotice("");
    void query.refetch().then((result) => {
      if (!result.isError) setReloadVersion((version) => version + 1);
    });
  };
  return (
    <article className="content-card form-stack" aria-labelledby="ai-settings-title">
      <div className="connection-heading">
        <div>
          <p className="eyebrow">Scheduled stock research</p>
          <h2 id="ai-settings-title">AI analysis settings</h2>
          <p className="section-description">
            Set the model, token prices and monthly allowance for all AI Analysis users in this
            environment.
          </p>
        </div>
        <Button tone="quiet" disabled={query.isFetching || save.isPending} onClick={reload}>
          Reload AI settings
        </Button>
      </div>
      {query.isPending ? (
        <ResourceState
          kind="loading"
          title="Loading AI settings"
          description="Reading this environment’s analysis settings."
        />
      ) : null}
      {query.isError ? (
        <ResourceState
          kind="error"
          title="AI settings unavailable"
          description="Reload the settings to try again."
        />
      ) : null}
      {query.data === undefined || query.isError ? null : (
        <>
          <p className="field-hint">
            Environment: <strong>{query.data.environment}</strong> · Analysis{" "}
            {query.data.enabled ? "enabled" : "disabled"}
            {query.data.source === "deployment"
              ? " · Using deployment defaults"
              : " · Using saved settings"}
          </p>
          {!query.data.secretConfigured ? (
            <p className="form-message form-message--error">
              The server API key is not configured. Settings can be saved, but analysis stays
              disabled until the key is configured in AWS.
            </p>
          ) : null}
          <SettingsForm
            key={`${query.data.environment}:${query.data.revision}:${reloadVersion}`}
            settings={query.data}
            pending={save.isPending}
            onSave={(values) => {
              setNotice("");
              save.mutate(values, {
                onSuccess: () =>
                  setNotice("AI analysis settings saved. New analyses will use these values."),
              });
            }}
          />
          {query.data.updatedAt === null ? null : (
            <p className="field-hint">
              Last saved {new Date(query.data.updatedAt).toLocaleString()}
            </p>
          )}
        </>
      )}
      {save.isError ? (
        <p className="form-message form-message--error" role="alert">
          {save.error.message} Your edits are still shown. Reload if another administrator changed
          these settings.
        </p>
      ) : null}
      {notice === "" ? null : (
        <p className="form-message form-message--success" role="status">
          {notice}
        </p>
      )}
    </article>
  );
}

function SettingsForm({
  settings,
  pending,
  onSave,
}: {
  readonly settings: AiAnalysisSettings;
  readonly pending: boolean;
  readonly onSave: (input: AiAnalysisSettingsInput) => void;
}) {
  const [model, setModel] = useState(settings.model);
  const [inputPrice, setInputPrice] = useState(
    settings.inputPerMillion > 0 ? String(settings.inputPerMillion) : "",
  );
  const [outputPrice, setOutputPrice] = useState(
    settings.outputPerMillion > 0 ? String(settings.outputPerMillion) : "",
  );
  const [budget, setBudget] = useState(String(settings.monthlyBudgetUsd));
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave({
      model: model.trim(),
      inputPerMillion: Number(inputPrice),
      outputPerMillion: Number(outputPrice),
      monthlyBudgetUsd: Number(budget),
      expectedRevision: settings.revision,
    });
  };
  return (
    <form className="form-stack" onSubmit={submit}>
      <div className="field-grid">
        <div className="field-stack">
          <label htmlFor="ai-settings-model">OpenAI model</label>
          <input
            id="ai-settings-model"
            required
            maxLength={200}
            pattern="[A-Za-z0-9][A-Za-z0-9._:\-]*"
            placeholder="e.g. gpt-4.1"
            value={model}
            onChange={(event) => setModel(event.target.value)}
            disabled={pending}
          />
        </div>
        <div className="field-stack">
          <label htmlFor="ai-settings-input">Input price per million tokens (USD)</label>
          <input
            id="ai-settings-input"
            type="number"
            required
            min="0.000001"
            max="10000"
            step="any"
            value={inputPrice}
            onChange={(event) => setInputPrice(event.target.value)}
            disabled={pending}
          />
        </div>
        <div className="field-stack">
          <label htmlFor="ai-settings-output">Output price per million tokens (USD)</label>
          <input
            id="ai-settings-output"
            type="number"
            required
            min="0.000001"
            max="10000"
            step="any"
            value={outputPrice}
            onChange={(event) => setOutputPrice(event.target.value)}
            disabled={pending}
          />
        </div>
        <div className="field-stack">
          <label htmlFor="ai-settings-budget">Monthly budget per user (USD)</label>
          <input
            id="ai-settings-budget"
            type="number"
            required
            min="0"
            max="100000"
            step="any"
            value={budget}
            onChange={(event) => setBudget(event.target.value)}
            disabled={pending}
          />
        </div>
      </div>
      <p className="field-hint">
        Use the model’s current standard USD prices from{" "}
        <a href="https://developers.openai.com/api/docs/pricing" target="_blank" rel="noreferrer">
          OpenAI pricing
        </a>
        . Prices estimate costs; they do not change OpenAI’s charges.
      </p>
      <p className="field-hint">
        The monthly allowance is shared across each user’s monitors. Set it to 0 to pause new
        analyses. Existing budget usage and completed reports are retained.
      </p>
      <Button type="submit" disabled={pending}>
        {pending ? "Saving…" : "Save AI analysis settings"}
      </Button>
    </form>
  );
}
